import React, { FC, useEffect, useState } from 'react';
import { Skeleton } from '@mui/material';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import CurrentTimeRepository from 'src/api/repositories/common/v1/currentTime.repository';
import { DATE_TIME_FORMAT, DAY_DATE_FORMAT } from 'src/shared/constants';
import { useTranslate } from 'src/shared/providers';
import { formatDateTime } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

interface Props {
  className?: string;
}

export const DashboardHeader: FC<Props> = ({ className }) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);
  const [currentDate, setCurrentDate] = useState<string | undefined>(undefined);
  const [isDateInitialized, setIsDateInitialized] = useState<boolean>(false);
  const { info, loading, infoError } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    const fetchCurrentTime = async () => {
      try {
        const repository = new CurrentTimeRepository();
        const { dateTime } = await repository.get();
        setCurrentDate(dateTime);
      } catch {
        setCurrentDate(moment().format(DATE_TIME_FORMAT));
      } finally {
        setIsDateInitialized(true);
      }
    };

    fetchCurrentTime();
  }, []);

  useEffect(() => {
    if (!isDateInitialized) {
      return;
    }

    const dateInterval = setInterval(() => {
      setCurrentDate((prevState) => moment(prevState).add(5, 'seconds').format(DATE_TIME_FORMAT));
    }, 5000);

    return () => {
      clearInterval(dateInterval);
    };
  }, [isDateInitialized, timeFormat]);

  if ((loading && !infoError) || !currentDate || !isDateInitialized) {
    return (
      <div className={className}>
        <Skeleton className="pb-2" variant="text" component="p" animation="wave" width={160} />
        <Skeleton variant="text" component="h2" animation="wave" width={140} />
      </div>
    );
  }

  return (
    <div className={className}>
      <p className="small mb-2">{formatDateTime(currentDate, DAY_DATE_FORMAT, timeFormat)}</p>
      {info && (
        <h3>
          {t('global.hi')} {info.firstName ? info.firstName : info.username}! 🙂
        </h3>
      )}
    </div>
  );
};
